import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button, Input, Select, Textarea } from '@dreamtree-org/twreact-ui';
import { ICON_OPTIONS } from '@site/content/schema';
import { getIn } from '@site/content/path';

const Hint = ({ children }) => (children ? <p className="mt-1 text-xs text-[var(--app-muted,#6b7280)]">{children}</p> : null);

function ImageInput({ label, value, onChange, hint }) {
  return (
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <Input label={label} value={value ?? ''} placeholder="/images/slider/example.jpg or https://..." onChange={(e) => onChange(e.target.value)} />
        <Hint>{hint}</Hint>
      </div>
      {value ? (
        <img
          src={value}
          alt=""
          className="mt-6 h-14 w-20 shrink-0 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] object-cover"
          onError={(e) => {
            e.currentTarget.style.visibility = 'hidden';
          }}
          onLoad={(e) => {
            e.currentTarget.style.visibility = 'visible';
          }}
        />
      ) : null}
    </div>
  );
}

function ScalarField({ type, label, hint, value, onChange }) {
  if (type === 'textarea') {
    return (
      <div>
        <Textarea label={label} rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
        <Hint>{hint}</Hint>
      </div>
    );
  }
  if (type === 'image') return <ImageInput label={label} value={value} hint={hint} onChange={onChange} />;
  if (type === 'icon') return <Select label={label} options={ICON_OPTIONS} value={value ?? ''} onChange={onChange} />;
  return (
    <div>
      <Input label={label} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      <Hint>{hint}</Hint>
    </div>
  );
}

function ItemShell({ title, index, count, onMove, onRemove, children }) {
  return (
    <div className="rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">{title} {index + 1}</p>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" aria-label={`Move ${title} ${index + 1} up`} disabled={index === 0} onClick={() => onMove(-1)}>
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label={`Move ${title} ${index + 1} down`} disabled={index === count - 1} onClick={() => onMove(1)}>
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label={`Remove ${title} ${index + 1}`} onClick={onRemove}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

const moveItem = (list, from, delta) => {
  const to = from + delta;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
};

function ListField({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const blank = Object.fromEntries(field.itemFields.map((f) => [f.key, f.type === 'icon' ? ICON_OPTIONS[0].value : '']));

  return (
    <fieldset className="space-y-3">
      <legend className="mb-1 text-sm font-medium">{field.label}</legend>
      {items.map((item, i) => (
        <ItemShell
          key={i}
          title={field.itemLabel}
          index={i}
          count={items.length}
          onMove={(d) => onChange(moveItem(items, i, d))}
          onRemove={() => onChange(items.filter((_, n) => n !== i))}
        >
          {field.itemFields.map((f) => (
            <ScalarField
              key={f.key}
              type={f.type}
              label={f.label}
              hint={f.hint}
              value={item[f.key]}
              onChange={(v) => onChange(items.map((it, n) => (n === i ? { ...it, [f.key]: v } : it)))}
            />
          ))}
        </ItemShell>
      ))}
      <Button variant="outline" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => onChange([...items, blank])}>
        Add {field.itemLabel.toLowerCase()}
      </Button>
    </fieldset>
  );
}

function StringsField({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  return (
    <fieldset className="space-y-3">
      <legend className="mb-1 text-sm font-medium">{field.label}</legend>
      {items.map((text, i) => (
        <div key={i} className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Input
              label={`${field.itemLabel} ${i + 1}`}
              value={text}
              onChange={(e) => onChange(items.map((t, n) => (n === i ? e.target.value : t)))}
            />
          </div>
          <Button variant="ghost" size="icon" aria-label={`Move ${field.itemLabel} ${i + 1} up`} disabled={i === 0} onClick={() => onChange(moveItem(items, i, -1))}>
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label={`Move ${field.itemLabel} ${i + 1} down`} disabled={i === items.length - 1} onClick={() => onChange(moveItem(items, i, 1))}>
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label={`Remove ${field.itemLabel} ${i + 1}`} onClick={() => onChange(items.filter((_, n) => n !== i))}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => onChange([...items, ''])}>
        Add {field.itemLabel.toLowerCase()}
      </Button>
    </fieldset>
  );
}

/** One schema field, bound to a page's draft content by dot path. */
export function ContentField({ field, content, onChange }) {
  const value = getIn(content, field.path);
  const set = (v) => onChange(field.path, v);
  if (field.type === 'list') return <ListField field={field} value={value} onChange={set} />;
  if (field.type === 'strings') return <StringsField field={field} value={value} onChange={set} />;
  return <ScalarField type={field.type} label={field.label} hint={field.hint} value={value} onChange={set} />;
}
